"use client";

import { Button } from "@speakai/ui/button";
import { Input, Textarea } from "@speakai/ui/input";
import { MultiCombobox } from "@/components/shared/multi-combobox";
import { useTranslation } from "react-i18next";
import { ChevronRight } from "lucide-react";

const NAME_MAX_LENGTH = 100;
const DESCRIPTION_MAX_LENGTH = 5000;

interface AssignToUser {
  id: string;
  name: string;
}

interface FolderDialogStepDetailsProps {
  name: string;
  setName: (value: string) => void;
  nameError: string | null;
  description: string;
  setDescription: (value: string) => void;
  descriptionError: string | null;
  assignTo: string[];
  setAssignTo: React.Dispatch<React.SetStateAction<string[]>>;
  showAssignTo: boolean;
  assignToList: AssignToUser[];
  isFormValid: boolean;
  isPending: boolean;
  isEdit: boolean;
  onSubmit: (e: React.FormEvent) => void | Promise<void>;
  onClose: () => void;
}

export function FolderDialogStepDetails({
  name,
  setName,
  nameError,
  description,
  setDescription,
  descriptionError,
  assignTo,
  setAssignTo,
  showAssignTo,
  assignToList,
  isFormValid,
  isPending,
  isEdit,
  onSubmit,
  onClose,
}: FolderDialogStepDetailsProps) {
  const { t } = useTranslation();

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4" data-testid="folder-dialog">
      <div className="flex flex-col gap-2" data-sentry-mask>
        <label htmlFor="folder-name" className="text-sm font-medium">
          Name
        </label>
        <Input
          id="folder-name"
          placeholder="Folder name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={NAME_MAX_LENGTH}
          autoFocus
          required
        />
        {name.length > 0 && nameError && (
          <p className="text-xs text-danger mt-1">{nameError}</p>
        )}
      </div>

      <div className="flex flex-col gap-2" data-sentry-mask>
        <label htmlFor="folder-description" className="text-sm font-medium">
          Description
        </label>
        <Textarea
          id="folder-description"
          placeholder="E.g., All my meetings with customers; Customer feedback; etc."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={DESCRIPTION_MAX_LENGTH}
          rows={3}
        />
        {descriptionError && (
          <p className="text-xs text-danger mt-1">{descriptionError}</p>
        )}
      </div>

      {showAssignTo && (
        <div className="flex flex-col gap-2" data-sentry-mask>
          <label
            htmlFor="folder-assign-to"
            className="text-sm font-medium"
          >
            Assign To
          </label>
          <MultiCombobox
            id="folder-assign-to"
            value={assignTo}
            onChange={(next) => setAssignTo(next)}
            options={assignToList.map((user) => ({
              value: user.id,
              label: user.name,
            }))}
            placeholder={
              assignToList.length === 0
                ? "No team members found"
                : "Select team members..."
            }
            searchPlaceholder="Search members..."
            emptyMessage="No matching members found"
            disabled={assignToList.length === 0}
            selectAllVisible
            aria-label="Assign team members to folder"
          />
          {assignTo.length > 0 && (
            <p aria-live="polite" aria-atomic="true" className="text-xs text-muted-foreground">
              {assignTo.length} {assignTo.length === 1 ? "member" : "members"} selected
            </p>
          )}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={isPending}
        >
          {t("ACTION.CANCEL")}
        </Button>
        <Button type="submit" disabled={!isFormValid || isPending}>
          {isPending
            ? isEdit
              ? t("ACTION.SAVING")
              : t("ACTION.CREATING")
            : isEdit
              ? t("ACTION.SAVE")
              : t("ACTION.NEXT")}
          {!isEdit && !isPending && (
            <ChevronRight className="h-4 w-4 ml-1" />
          )}
        </Button>
      </div>
    </form>
  );
}
